import { useState, useCallback, useRef, useEffect } from 'react';
import { useSSEStream } from '../../hooks/useSSEStream';
import useChatSessions from './useChatSessions';
import { interactiveServiceFetch } from '../../services/interactiveServiceFetch';
import { MESSAGE_ROLES, STEP_STATUS, EVENT_TYPES, ERROR_TYPES } from '../constants';

const createMessageId = () => `${MESSAGE_ROLES.ASSISTANT}-${Date.now()}`;

const SSE_COMPLETION_EVENTS = [
    EVENT_TYPES.COMPLETED,
    EVENT_TYPES.ERROR,
    EVENT_TYPES.USER_ERROR,
    EVENT_TYPES.CANCELLED,
];

const createAssistantMessage = messageId => ({
    id: messageId,
    role: MESSAGE_ROLES.ASSISTANT,
    content: [], // Content is an array of blocks: [{type: 'markdown', data: '...'}, {type: 'code', data: {...}}, ...]
    isThinking: true,
    isStreaming: false,
    createdAt: new Date(),
    steps: [],
});

const updateMessageSteps = (existingSteps, newStep) => {
    const stepIndex = existingSteps.findIndex(s => s.step_id === newStep.step_id);
    const updatedSteps =
        stepIndex >= 0
            ? existingSteps.map((step, idx) => (idx === stepIndex ? { ...step, ...newStep } : step))
            : [...existingSteps, newStep];

    return {
        steps: updatedSteps,
        isThinking: updatedSteps.some(s => s.status === STEP_STATUS.RUNNING),
    };
};

const useChat = ({ api, onFinish } = {}) => {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    const [error, setError] = useState(null);
    const [currentRunId, setCurrentRunId] = useState(null);
    const [stopError, setStopError] = useState(null);
    const currentAssistantMessageIdRef = useRef(null);
    const onFinishRef = useRef(onFinish);
    const inputRef = useRef('');

    useEffect(() => {
        onFinishRef.current = onFinish;
    }, [onFinish]);

    useEffect(() => {
        inputRef.current = input;
    }, [input]);

    const onSessionChange = useCallback((sessionId, shouldClearMessages, loadedMessages = null, sessionError = null) => {
        if (shouldClearMessages) setMessages([]);
        else if (loadedMessages) setMessages(loadedMessages);

        setError(sessionError);
        setStopError(null);
    }, []);

    const {
        currentSessionId,
        setCurrentSessionId,
        sessions,
        isLoadingSessions,
        fetchSessions,
        upsertSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
    } = useChatSessions({ api, onSessionChange });

    const resetChatState = useCallback(() => {
        setIsLoading(false);
        setCurrentRunId(null);
        setStopError(null);
        currentAssistantMessageIdRef.current = null;
    }, []);

    const clearCurrentSession = useCallback(() => {
        setCurrentSessionId(null);
        setMessages([]);
        setError(null);
    }, [setCurrentSessionId]);

    const handleError = useCallback((errorMessage, isUserError = false) => {
        const assistantMessageId = currentAssistantMessageIdRef.current;
        resetChatState();
        setError(null);

        if (!isUserError) {
            setError(errorMessage);
            return;
        }

        const textContent = [{ type: 'error', data: errorMessage }];
        if (!assistantMessageId) {
            setMessages(prev => [...prev, { ...createAssistantMessage(createMessageId()), isThinking: false, content: textContent }]);
        } else {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === assistantMessageId
                        ? { ...msg, isThinking: false, isStreaming: false, steps: [], content: [...(msg.content || []), ...textContent] }
                        : msg
                )
            );
        }
    }, [resetChatState]);

    // Lifecycle event handlers
    const handleStarted = useCallback(() => {
        const messageId = createMessageId();
        currentAssistantMessageIdRef.current = messageId;
        setMessages(prev => [...prev, createAssistantMessage(messageId)]);
    }, []);

    const handleCompleted = useCallback(() => {
        setMessages(prev => {
            const updatedMessages = prev.map(msg => {
                if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                const completedSteps = (msg.steps || []).map(step =>
                    step.status !== STEP_STATUS.FAILED ? { ...step, status: STEP_STATUS.COMPLETED } : step
                );
                return { ...msg, isThinking: false, isLoading: false, isStreaming: false, steps: completedSteps };
            });

            const finishedMessage = updatedMessages.find(m => m.id === currentAssistantMessageIdRef.current);
            if (finishedMessage) {
                onFinishRef.current?.(finishedMessage);
            }

            return updatedMessages;
        });
        resetChatState();
    }, [resetChatState]);

    const handleCancelled = useCallback(message => {
        const assistantMessageId = currentAssistantMessageIdRef.current;
        resetChatState();

        if (!assistantMessageId) return;

        const displayedMessage = message?.message ?? '**Response was interrupted**';
        setMessages(prev =>
            prev.map(msg => {
                if (msg.id !== assistantMessageId) return msg;
                return {
                    ...msg,
                    isThinking: false,
                    isStreaming: false,
                    content: Array.isArray(msg.content) && msg.content.length
                        ? [...msg.content, { type: 'markdown', data: `\n\n${displayedMessage}` }]
                        : [{ type: 'markdown', data: displayedMessage }],
                };
            })
        );
    }, [resetChatState]);

    // Content event handlers
    const handleToken = useCallback(message => {
        const content = message.content || message.token || '';
        if (content && currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Get the last content block
                    const lastBlock = updatedContent[updatedContent.length - 1];

                    // If last block is markdown, append to it; otherwise create new markdown block
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += content;
                    } else {
                        updatedContent.push({ type: 'markdown', data: content });
                    }

                    return { ...msg, content: updatedContent, isThinking: false, isStreaming: true };
                })
            );
        }
    }, []);

    const handleStepUpdate = useCallback(message => {
        const { step } = message;
        if (step && currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg =>
                    msg.id === currentAssistantMessageIdRef.current
                        ? { ...msg, ...updateMessageSteps(msg.steps || [], step) }
                        : msg
                )
            );
        }
    }, []);

    const handleArtifact = useCallback(message => {
        const { artifact } = message;
        if (artifact && currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Add artifact as a content block
                    updatedContent.push(artifact);

                    return { ...msg, content: updatedContent };
                })
            );
        }
    }, []);

    const handleResponseComplete = useCallback(() => {
        if (currentAssistantMessageIdRef.current) {
            setMessages(prev =>
                prev.map(msg => {
                    if (msg.id !== currentAssistantMessageIdRef.current) return msg;

                    const updatedContent = Array.isArray(msg.content) ? [...msg.content] : [];

                    // Add line break to last markdown block if it exists
                    const lastBlock = updatedContent[updatedContent.length - 1];
                    if (lastBlock && lastBlock.type === 'markdown') {
                        lastBlock.data += '\n\n';
                    }

                    return { ...msg, isStreaming: false, content: updatedContent };
                })
            );
        }
    }, []);

    // Event routing
    const handleSSEMessage = useCallback(
        message => {
            const { type } = message;

            // Route lifecycle events
            if (type === EVENT_TYPES.STARTED) return handleStarted();
            if (type === EVENT_TYPES.COMPLETED) return handleCompleted();
            if (Object.values(ERROR_TYPES).includes(type))
                return handleError(message.message || message.error || 'An error occurred', type === ERROR_TYPES.USER_ERROR);
            if (type === EVENT_TYPES.CANCELLED) return handleCancelled(message);
            if (type === EVENT_TYPES.RESPONSE_COMPLETE) return handleResponseComplete();
            if (type === EVENT_TYPES.SESSION_TITLE_UPDATED) return upsertSession(message.session_id, message.title);

            // Route content events (require active message)
            if (!currentAssistantMessageIdRef.current) return;

            if (type === EVENT_TYPES.TOKEN || type === EVENT_TYPES.TOKEN_BATCH) return handleToken(message);
            if (type === EVENT_TYPES.STEP_UPDATE) return handleStepUpdate(message);
            if (type === EVENT_TYPES.ARTIFACT) return handleArtifact(message);
        },
        [
            handleStarted,
            handleCompleted,
            handleError,
            handleCancelled,
            handleResponseComplete,
            upsertSession,
            handleToken,
            handleStepUpdate,
            handleArtifact,
        ]
    );

    const { isConnected: sseConnected, error: sseError } = useSSEStream(api, currentRunId, handleSSEMessage, SSE_COMPLETION_EVENTS);

    useEffect(() => {
        if (sseError) handleError(sseError);
    }, [sseError, handleError]);

    const handleSubmit = useCallback(
        async (e, options = {}) => {
            e?.preventDefault();

            const messageContent = options?.content || inputRef.current;

            if (!messageContent.trim() || isLoading) return;

            setError(null);
            setStopError(null);
            setIsLoading(true);

            const userMessage = {
                id: `${MESSAGE_ROLES.USER}-${Date.now()}`,
                role: MESSAGE_ROLES.USER,
                content: [{ type: 'markdown', data: messageContent }],
                createdAt: new Date(),
            };

            setMessages(prev => [...prev, userMessage]);
            setInput('');

            try {
                const response = await interactiveServiceFetch(`${api}/runs`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        user_query: messageContent,
                        request_id: `req-${Date.now()}`,
                        session_id: currentSessionId,
                    }),
                });

                if (!response.ok) {
                    throw new Error(`Failed to create run: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();
                const runId = result.run_id;

                setCurrentRunId(runId);
                setCurrentSessionId(result.session_id || currentSessionId);
            } catch (err) {
                handleError(err.message || 'An error occurred');
            }
        },
        [isLoading, api, currentSessionId, setCurrentSessionId, handleError]
    );

    const append = useCallback(
        async message => {
            await handleSubmit(null, { content: message.content });
        },
        [handleSubmit]
    );

    const reload = useCallback(async () => {
        const lastUserMessage = [...messages].reverse().find(m => m.role === MESSAGE_ROLES.USER);
        if (!lastUserMessage) return;

        const userMessageIndex = messages.findIndex(m => m.id === lastUserMessage.id);
        setMessages(messages.slice(0, userMessageIndex + 1));

        await handleSubmit(null, { content: lastUserMessage.content?.[0]?.data });
    }, [messages, handleSubmit]);

    const stop = useCallback(async () => {
        if (!currentRunId) return;
        setStopError(null);
        try {
            const assistantMessageId = currentAssistantMessageIdRef.current;
            const partialContent = assistantMessageId
                ? messages.find(m => m.id === assistantMessageId)?.content ?? []
                : [];
            await interactiveServiceFetch(`${api}/runs/${currentRunId}/cancel`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    session_id: currentSessionId,
                    partial_content: partialContent,
                }),
            });
        } catch (err) {
            // Cancel API failed - backend is still running, keep streaming as normal
            setStopError('Failed to interrupt query');
        }
    }, [currentRunId, api, currentSessionId, messages]);

    const handleInputChange = useCallback(e => setInput(e.target.value), []);

    return {
        messages,
        input,
        setInput,
        handleSubmit,
        handleInputChange,
        isLoading,
        error,
        stopError,
        append,
        reload,
        stop,
        setMessages,
        sseConnected,
        // Session management
        currentSessionId,
        sessions,
        isLoadingSessions,
        clearCurrentSession,
        loadSession,
        deleteSession,
        updateSessionTitle,
        fetchSessions,
    };
};

export default useChat;
